import { throttle } from 'lodash';
import React, { useMemo, useState, useEffect } from 'react';
import { Autocomplete } from '@mui/material';
import { fetchAutocompleteResults } from '../autocomplete/autocomplete-helpers';
import { AutocompleteResult } from '../autocomplete/autocomplete-result';

interface Props {
    isTcsm?: boolean;
    placeHolderText?: string;
    showResultsInTab?: boolean;
}

export function GlobalSearch(props: Props) {

    const [searchTerm, setSearchTerm] = useState('');
    const [autocompleteOptions, setAutocompleteOptions] = useState([]);

    const fetch = useMemo(() =>
        throttle(
            async (
                query: string,
                callback: (results?: string[]) => void,
            ) => {
                const hits = await fetchAutocompleteResults(query);
                callback(hits?.map(hit => hit?.query));
            },
            200,
        ),
        [],
    );

    // async fetch of autocomplete results when searchterm changes
    useEffect(() => {
        let active = true;
        if (searchTerm === '') {
            setAutocompleteOptions([]);
            return undefined;
        }

        if (searchTerm.length >= 3) {
            fetch(searchTerm, (results: string[]) => {
                if (active) {
                    setAutocompleteOptions(results);
                }
            });
        } else if (searchTerm.length === 0) {
            setAutocompleteOptions([]);
        }

        return () => {
            active = false;
        };

    }, [searchTerm, fetch]);

    const onInputChange = async (value: string) => {
        setSearchTerm(value);
    };

    const onChange = (value: string) => {
        if (value) {            
            const resultUrl = `/search/?q=${encodeURIComponent(value)}`;
            if (props.showResultsInTab) {
                window.open(resultUrl);
            } else {
                window.location.href = resultUrl;
            }
        }
    };

    const getSearchBar = (params) => {
        if (props.isTcsm) {
            return (
                <div className='nav-search-inner' ref={params.InputProps.ref}>
                    <input type='text' {...params.inputProps} />
                    <div className='nav-search-actions'>
                        {
                            searchTerm &&
                            <button
                                className='nav-search-button nav-search-clear'
                                type='button'
                                onClick={() => setSearchTerm('')}><svg className='icon' aria-hidden='true' focusable='false'>
                                    <use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#input-clear-icon' />
                                </svg><span className='visually-hidden'>Clear Field</span></button>
                        }
                        <button className='nav-search-button'
                            type='button'
                            onClick={() => onChange(searchTerm)}>
                            <svg focusable='false' className='svg-icon'>
                                <use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#search-icon' />
                            </svg>
                            <span className='visually-hidden'>Search</span></button>
                    </div>
                </div>
            );
        }
        return (
            <>
                <div className='adage-header-utility-search-bar' ref={params.InputProps.ref}>

                    <input type='text' {...params.inputProps} placeholder={props.placeHolderText} />
                    {
                        searchTerm &&
                        <button className='un-button adage-header-utility-search-bar-clear'
                            type='button'
                            onClick={() => setSearchTerm('')}>
                            <svg focusable='false'>
                                <use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#close-icon' />
                            </svg><span className='visually-hidden'>Clear Field</span></button>
                    }

                </div>
                <button className='un-button adage-header-utility-search-bar-button'
                    type='button'
                    onClick={() => onChange(searchTerm)}>
                    <svg focusable='false' className='svg-icon'>
                        <use xmlnsXlink='http://www.w3.org/2000/xlink' xlinkHref='#search-icon' />
                    </svg>
                    <span className='visually-hidden'>Search</span></button>
            </>
        );
    };

    return (
        <>
            <Autocomplete
                inputValue={searchTerm}
                freeSolo
                autoComplete
                id='global-search'
                onInputChange={(_e, value) => onInputChange(value)}
                onChange={(_e, value) => onChange(value)}
                filterOptions={(x) => x}
                options={autocompleteOptions || []}
                sx={{ width: 300 }}
                renderInput={(params) => getSearchBar(params)}
                renderOption={(prop, option: string) => (
                    <AutocompleteResult
                        key={option}
                        passedProps={prop}
                        option={option}
                        searchTerm={searchTerm}
                    />
                )}
            />
        </>
    );
}

GlobalSearch.defaultProps = {
    isTcsm: false,
    placeHolderText: '',
    showResultsInTab: true,
};